import {
  AppInfo,
  Button,
  Header,
  Icon,
  ListItem,
  MaleCard,
  SettingEmail,
  SettingNotification,
  Text,
  LineAddFriendModalContent,
  Modal,
  LineLoginModalContent,
} from 'components';
import { TEXT_STRING, routes } from '../../constants';
import { SHOW_SALON_GACHA_TICKETS } from '../../constants/featureFlags';
import { useNavigate } from 'react-router-dom';
import { useSettingStore, useUserStore } from 'store';
import { useAuth, useLine } from 'hooks';
import Banner from '../../assets/images/advertisingBanner.png';
import { useEffect, useMemo } from 'react';
import bannerYarikanoImage from '../../assets/images/groupServicesImages/banner_mypage_yarikano.png';
import bannerHeadyImage from '../../assets/images/groupServicesImages/banner_mypage_heady.png';
import bannerHimitsuImage from '../../assets/images/groupServicesImages/banner_mypage_himitsu.png';
import bannerTecoPicksImage from '../../assets/images/groupServicesImages/banner_mypage_tecopicks.png';
import bannerYoutubeImage from '../../assets/images/groupServicesImages/banner_mypage_youtube.jpg';
import bannerAaanImage from '../../assets/images/groupServicesImages/banner_mypage_aaan.png';

const COMMON_STR = TEXT_STRING.COMMON;
const MY_PAGE_STR = TEXT_STRING.MY_PAGE;

function MyPage() {
  // Hook
  const { setOpenTutorial } = useSettingStore();
  const { user } = useUserStore();
  const { logout } = useAuth();
  const { lineGate, ensureLineGate, clearLineGate, loginOrRedirectLineOAuth } =
    useLine();
  const navigate = useNavigate();

  // Memo
  const accountInformation = useMemo(
    () => [
      {
        route: routes.FOLLOWING,
        label: MY_PAGE_STR.LIST_FOLLOWING,
        icon: 'wifi',
      },
      {
        route: routes.FEMALE_MET,
        label: MY_PAGE_STR.FEMALE_MET,
        icon: 'doubleHeart',
      },
      {
        route: routes.ORDER_HISTORY,
        label: MY_PAGE_STR.ORDER_HISTORY,
        icon: 'paper',
      },
      ...(SHOW_SALON_GACHA_TICKETS
        ? [
            {
              route: routes.GACHA_TICKETS,
              label: MY_PAGE_STR.GACHA_TICKETS,
              icon: 'paper',
            },
          ]
        : []),
      {
        route: routes.BUY_POINTS,
        label: MY_PAGE_STR.BUY_POINTS,
        icon: 'teco',
        disabled: user?.isLimited,
      },
      {
        route: routes.POINT_HISTORY,
        label: MY_PAGE_STR.BUY_POINTS_HISTORY,
        icon: 'fileClock',
        disabled: user?.isLimited,
      },
      {
        route: routes.PREMIUM_PURCHASED,
        label: MY_PAGE_STR.PURCHASED_PAID_TWEET,
        icon: 'computer',
      },
    ],
    [user?.isLimited],
  );

  const handleExternalLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const bannerList = [
    {
      src: bannerYarikanoImage,
      alt: 'yk2024',
      url: 'https://yk2024.com/home?utm_source=tecoapp&utm_medium=banner&utm_campaign=1',
    },
    {
      src: bannerHeadyImage,
      alt: 'heady2025',
      url: 'https://heady2025.com/?utm_source=tecoapp&utm_medium=banner&utm_campaign=1',
    },
    {
      src: bannerHimitsuImage,
      alt: 'beauty-kc',
      url: 'https://beauty-kc.jp/?utm_source=tecoapp&utm_medium=banner&utm_campaign=3',
    },
    {
      src: bannerAaanImage,
      alt: 'aaan',
      url: 'https://tmm-com.jp/recruit/?utm_source=tecoapp&utm_medium=banner&utm_campaign=1',
    },
    {
      src: bannerTecoPicksImage,
      alt: 'tecopicks',
      url: 'https://tecopicks.com/?utm_source=tecoapp&utm_medium=banner&utm_campaign=1',
    },
    {
      src: bannerYoutubeImage,
      alt: 'youtube',
      url: 'https://youtube.com/@tecopicks?si=akolZnBLFkICvdfe',
    },
  ];

  const biography = user?.profileCollection?.biography?.content || '';
  const isSalonMember = !!user?.isSalonMember;
  const isSalonPremiumMember = !!user?.isSalonPremiumMember;

  // Effect
  useEffect(() => {
    if (!user) return;
    (async () => {
      await ensureLineGate();
    })();
  }, [ensureLineGate, user]);

  return (
    <div className="h-full overflow-y-auto">
      <Header
        title={user?.name || COMMON_STR.MY_PAGE}
        suffix={
          <Button
            onClick={() => navigate(routes.EDIT_MALE)}
            padding="py-2 px-4"
            className="whitespace-nowrap"
            rounded='rounded-full'
          >
            {TEXT_STRING.MALE_PAGE.EDIT_PROFILE}
          </Button>
        }
      />
      <Modal
        open={lineGate === 'LINE' || lineGate === 'LINE_FRIEND'}
        onClose={clearLineGate}
        className="px-5 py-6 mx-5 bg-white w-full flex justify-center items-center"
      >
        {lineGate === 'LINE' && (
          <LineLoginModalContent
            onCancel={clearLineGate}
            onClick={loginOrRedirectLineOAuth}
            loading={false}
            disabled={false}
          />
        )}
        {lineGate === 'LINE_FRIEND' && (
          <LineAddFriendModalContent
            onSuccess={clearLineGate}
            onClose={clearLineGate}
          />
        )}
      </Modal>
      <div className="px-16px mt-4">
        <MaleCard
          name={user?.name}
          avatar={user?.avatar}
          biography={biography}
          ranking={user?.total.ranking}
          isSalonMember={isSalonMember}
          isSalonPremiumMember={isSalonPremiumMember}
          onOpenRank={() => navigate(routes.STAR_DETAIL)}
        />
        <div className="flex flex-col gap-12px py-20px border-b border-neutral-50">
          <img
            src={Banner}
            className="cursor-pointer"
            alt="introducing-friend"
            onClick={() => {
              navigate(routes.INTRODUCING_FRIEND);
            }}
          />
          {bannerList.map((banner, index) => (
            <img
              key={index}
              src={banner.src}
              className="cursor-pointer shadow-lg"
              alt={banner.alt}
              onClick={() => handleExternalLink(banner.url)}
            />
          ))}
          <Text textColor="text-primary" fontSize={14} bold>
            {MY_PAGE_STR.ACCOUNT_INFO}
          </Text>
          <Text bold>{COMMON_STR.INFO}</Text>
          <Text>
            {MY_PAGE_STR.ACCOUNT_INFO}:{' '}
            <span className="ml-8">{user?.name || ''}</span>
          </Text>
          {accountInformation.map((item, index) => (
            <ListItem
              key={index}
              onClick={() => navigate(item.route)}
              text={item.label}
              prefix={
                <div>
                  <Icon icon={item.icon} color="var(--color-neutral-550)" />
                </div>
              }
              className="cursor-pointer"
              disabled={item?.disabled}
            />
          ))}
        </div>
        <div className="py-20px flex flex-col gap-3 border-b border-neutral-50">
          <Text textColor="text-primary" fontSize={14} bold>
            {COMMON_STR.SETTING}
          </Text>
          <Text bold>{COMMON_STR.PROFILE}</Text>
          <ListItem
            className="cursor-pointer"
            text={TEXT_STRING.MY_PAGE.OPEN_TUTORIAL}
            onClick={() => setOpenTutorial(true, 'mypage')}
            prefix={<Icon icon={'home'} color="var(--color-neutral-550)" />}
          />
          {/* <ListItem
            className="cursor-pointer"
            text={MY_PAGE_STR.LINE_LINK}
            prefix={<Icon icon={'line'} />}
            suffix={
              <ToggleButton
                isToggle={!user?.inactiveLine}
                onToggle={handleLineLinkToggle}
                isLoading={loadingAccount || isPending || isFetchingUserInfo}
              />
            }
          /> */}
          <SettingNotification />
          <SettingEmail />
        </div>
        <AppInfo />
        <div className="py-20px">
          <ListItem
            onClick={logout}
            text={TEXT_STRING.MY_PAGE.LOGOUT}
            textColor="text-error"
            prefix={<Icon icon={'logOut'} />}
          />
        </div>
      </div>
    </div>
  );
}

export default MyPage;
